import { types, getEnv, flow, SnapshotOrInstance, Instance, SnapshotOut } from 'mobx-state-tree';
import { IApiDate2StringConverter } from '../../api/apiBase';
import {
    IGroupedApiCtx,
    ProgressStateModelForStore, errorIsCancel, ProgressStateModel,
    ICommonAppEnvWithSBBaseApi,
} from 'eTip--common';
import { observable } from 'mobx';
import { translationForMts } from 'easit--localization';
import { OfferType, TicketSelectionEvalState, TicketState } from '../../api/proto/apiContract';
import { ITicketDetailForVGMarketResponse, ITicketDetailForVGResponse } from '../../dtos/ticketForVirtualGamesDtos';
import { TicketDetaiForVGSelectionStore } from './ticketDetaiForVGSelection';
import { flatten } from 'easit--utils';
import { IVirtualGamesApi, TicketStatusVG, TicketTypeForVG } from '../../apiVirtualgames/apiVirtualGames';
import { ICommonAppEnvWithVirtualGames } from 'eTip--common';

export const defaultTicketDetailForVGStore = (): Instance<typeof TicketDetailForVGStore> => {
    let data: any = {};
    resetValuesInStore(data as Instance<typeof TicketDetailForVGStore>);
    return data;
}

const resetValuesInStore = (store: SnapshotOrInstance<typeof TicketDetailForVGStore>): void => {
    store.id = '';
    store.offerType = OfferType.Prematch; //as a default value
    store.winToPay = 0;
    store.potentialWin = 0;
    store.placementDate = null;
    store.rate = 0;
    store.ticketType = TicketTypeForVG.Single;
    //store.stakeType = StakeType.All;
    store.stake = 0;
    //store.stakePoints = null;
    //store.handingFee = 0;
    //store.handingFeePoints = null;
    store.error = null;
    store.selections = observable([]);
    store.number = '';

    store.ticketState = TicketState.None;

    store.state = <ProgressStateModel>'notInitialized';
}

export const TicketDetailForVGStore = types
    .model('ticketDetailForVGStore', {
        id: types.string, //must be string since it may be long (bigger than Number)
        evalState: types.maybeNull(types.number), //EvalState
        ticketState: types.maybeNull(types.number), //TicketState
        offerType: types.number, //OfferType
        winToPay: types.maybeNull(types.number),
        potentialWin: types.maybeNull(types.number),
        finalWin: types.maybeNull(types.number),
        placementDate: types.maybeNull(types.Date),
        paidDate: types.maybeNull(types.Date),
        rate: types.maybeNull(types.number),
        ticketType: types.maybeNull(types.enumeration([TicketTypeForVG.Single, TicketTypeForVG.System, TicketTypeForVG.Split, TicketTypeForVG.Combi])), //TicketTypeForVG
        stake: types.number,
        //stakePoints: types.maybeNull(types.number), //null means not set
        //stakeType: types.number, //StakeType
        //handingFee: types.number,
        //handingFeePoints: types.maybeNull(types.number),
        error: types.maybeNull(types.string),
        selections: types.array(TicketDetaiForVGSelectionStore),
        number: types.maybeNull(types.string),
        //productFee: types.maybeNull(MoneyWithCurrency),
        //productName: types.maybeNull(types.string),
        //oddsRateAdvantage: types.maybeNull(types.number),
        //rateAdvantageFactor: types.maybeNull(types.number),
        customerNickName: types.maybeNull(types.string),

        state: types.optional(ProgressStateModelForStore, ProgressStateModel.NotInitialized),
    })
    .views(_self => {
        //const env = getEnv<ICommonAppEnvWithSBBaseApi>(self);
        return {
            get offerTypeForUser() {
                return "Virtuálne hry";
            }
        }
    })
    .actions(self => {
        const env = getEnv<ICommonAppEnvWithSBBaseApi>(self);
        const envVG = getEnv<ICommonAppEnvWithVirtualGames>(self);
        let synchroFullLoadCtx: IGroupedApiCtx<IVirtualGamesApi> | null = null;
        const t = translationForMts();

        const setData = (ticket: ITicketDetailForVGResponse, string2Date: IApiDate2StringConverter): void => {
            env.loggingService.info(ticket);

            if (ticket.bets.length != 1) {
                throw new Error("There is none or more than 1 bet");
            }

            self.id = ticket.barcode.toString();
            self.offerType = OfferType.Prematch;
            self.ticketState = virtualGamesLoadHelper.prepareTicketState(ticket.status);
            self.finalWin = ticket.win;
            self.potentialWin = ticket.potentialWin;
            self.winToPay = ticket.win;
            self.placementDate = string2Date(ticket.createdDate);
            self.paidDate = string2Date(ticket.payoutDate);
            self.rate = ticket.rate;
            self.ticketType = ticket.bets[0].combinationType;
            //self.stakeType = ticket.Base.StakeType;
            self.evalState = virtualGamesLoadHelper.prepareSelectionEvalState(ticket.status);
            self.number = ticket.barcode.toString();

            if (env.settings.canShowExtendedTicketDetail) {
                self.customerNickName = ticket.playerNickname;
            }

            self.stake = ticket.stake;

            setMarkets(flatten(ticket.bets.map(bet => bet.markets)));
        }

        const setMarkets = (markets: ITicketDetailForVGMarketResponse[]) => {
            self.selections = observable([]);
            markets.forEach(selection => {
                self.selections.push(TicketDetaiForVGSelectionStore.create(<SnapshotOut<typeof TicketDetaiForVGSelectionStore>>{
                    id: selection.eventId,
                    number: selection.eventId,
                    evalState: virtualGamesLoadHelper.prepareSelectionEvalState(selection.status),
                    fixtureName: selection.gameAdditionalInfo ? `${selection.gameType} ${selection.gameAdditionalInfo}` : selection.gameType,
                    rate: selection.rate,
                    result: selection.eventResult,
                    tipName: selection.selection,
                    gameType: selection.gameType,
                    betType: selection.betType
                }))
            })
        }

        const loadMyTicketAsync: (ticketId: string, isReload?: boolean) => Promise<void> = flow(function* (ticketId: string, isReload?: boolean) {
            if (!isReload) {
                resetValuesInStore(self);
            }
            self.id = ticketId;

            yield loadDataRawAsync(
                (api: IVirtualGamesApi): Promise<ITicketDetailForVGResponse | null> => {
                    return api.getTicketDetailForVGAsync({ barcode: ticketId });
                },
                (data: ITicketDetailForVGResponse, string2Date: IApiDate2StringConverter) => { setData(data, string2Date); },
                isReload);
        });

        const loadDataRawAsync: (
            loader: (api: IVirtualGamesApi) => Promise<ITicketDetailForVGResponse | null>,
            dataSetter: (data: ITicketDetailForVGResponse | string | null, string2Date: IApiDate2StringConverter) => void,
            isSilent?: boolean) =>
            Promise<void> = flow(function* (
                loader: (api: IVirtualGamesApi) => Promise<ITicketDetailForVGResponse | null>,
                dataSetter: (data: ITicketDetailForVGResponse | string | null, string2Date: IApiDate2StringConverter) => void,
                isSilent?: boolean) {

                if (env.loggedUserId <= 0) {
                    self.state = ProgressStateModel.Failed;
                    self.error = t.ticket_Login;
                    return;
                }

                if (someOperationInProgress(false)) {
                    env.loggingService.error("Some operation already in progress!");
                    throw new Error("Some operation already in progress!");
                }

                if (synchroFullLoadCtx && synchroFullLoadCtx.isOpen()) {
                    synchroFullLoadCtx.cancel();
                    synchroFullLoadCtx = null;
                }

                if (!isSilent) {
                    self.state = ProgressStateModel.Loading;
                }
                let ctx: IGroupedApiCtx<IVirtualGamesApi> | null = null;
                try {

                    ctx = envVG.virtualGamesApi.open();
                    synchroFullLoadCtx = ctx;

                    let r: ITicketDetailForVGResponse | null = yield loader(ctx.api);
                    if (!r) {
                        self.error = t.ticket_NotFound;
                        self.state = ProgressStateModel.Failed;
                        return;
                    }
                    dataSetter(r, ctx.api.toDate);
                    self.state = ProgressStateModel.Success;
                } catch (error) {
                    if (errorIsCancel(error)) {
                        throw error;
                    }
                    env.loggingService.error(error);
                    self.state = ProgressStateModel.Failed;
                    self.error = t.general_ProgressContentError;
                    throw error;
                } finally {
                    if (synchroFullLoadCtx === ctx) {
                        synchroFullLoadCtx = null;
                    }
                }
            });

        const someOperationInProgress = (includingLoad: boolean): boolean => {
            return (includingLoad && synchroFullLoadCtx !== null);
        }

        return {
            loadMyTicketAsync,
        }
    });

export const virtualGamesLoadHelper = {

    prepareSelectionEvalState: (status: TicketStatusVG): TicketSelectionEvalState => {
        if (status === TicketStatusVG.Winning) {
            return TicketSelectionEvalState.Win;
        }
        if (status === TicketStatusVG.Lost) {
            return TicketSelectionEvalState.Loss;
        }
        if (status === TicketStatusVG.Cancelled) {
            return TicketSelectionEvalState.Cancel;
        }
        return TicketSelectionEvalState.None;
    },

    prepareTicketState: (status: TicketStatusVG): TicketState => {
        if (status === TicketStatusVG.Lost || status === TicketStatusVG.Winning) {
            return TicketState.Evaluated;
        }
        if (status === TicketStatusVG.Cancelled) {
            return TicketState.Storno;
        }
        return TicketState.Paid;
    }
}